import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const SOUND_FILES = [
  { name: "Button Click", path: "/sounds/ui/button-click.mp3" },
  { name: "Card Place", path: "/sounds/gameplay/card-place.mp3" },
  { name: "Card Flip", path: "/sounds/gameplay/card-flip.mp3" },
  { name: "Card Deal", path: "/sounds/gameplay/card-deal.mp3" },
  { name: "Shuffle", path: "/sounds/ui/shuffle.mp3" },
  { name: "Win", path: "/sounds/win/game-win.mp3" },
  { name: "Lose", path: "/sounds/lose/game-lose.mp3" },
];

export default function SoundTest() {
  const [volume, setVolume] = useState(0.5);
  const [status, setStatus] = useState<Record<string, string>>({});
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  // Initialize the audio context on page load
  useEffect(() => {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContext) {
        const ctx = new AudioContext();
        setAudioContext(ctx);
        
        // Log audio context state
        console.log(`Audio context created with state: ${ctx.state}`);
        
        if (ctx.state === 'suspended') {
          console.log('Audio context is suspended, will need user interaction to resume');
        }
      } else {
        console.error('AudioContext not supported in this browser');
        setStatus({ error: 'AudioContext not supported in this browser' });
      }
    } catch (err) {
      console.error('Error creating AudioContext:', err);
      setStatus({ error: `Error creating AudioContext: ${err}` });
    }

    // Cleanup
    return () => {
      if (audioContext) {
        audioContext.close().catch(console.error);
      }
    };
  }, []);

  // Handler for "Initialize Audio" button - required on some browsers/devices
  const handleInitAudio = () => {
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume().then(() => {
        console.log(`Audio context resumed: ${audioContext.state}`);
        setStatus(prev => ({ ...prev, audioContext: `Ready: ${audioContext.state}` }));
      }).catch(err => {
        console.error('Failed to resume audio context:', err);
        setStatus(prev => ({ ...prev, audioContext: `Error: ${err.message}` }));
      });
    } else {
      setStatus(prev => ({ ...prev, audioContext: 'Already initialized' }));
    }
    
    // Play a silent sound to kickstart audio on iOS
    const silentSound = new Audio("data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");
    silentSound.play().catch(console.error);
  };

  // Play a sound file with direct Audio API
  const playSound = (path: string) => {
    try {
      // Resume audio context if needed
      if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume().catch(console.warn);
      }

      // Create a new Audio element for each sound (better for overlapping sounds)
      const audio = new Audio(path);
      audio.volume = volume;
      
      // Log before attempting to play
      console.log(`Attempting to play: ${path} with volume ${volume}`);
      
      // Set status to loading
      setStatus(prev => ({ ...prev, [path]: 'Loading...' }));
      
      // Set up event listeners
      audio.addEventListener('canplaythrough', () => {
        console.log(`Sound ready to play: ${path}`);
        setStatus(prev => ({ ...prev, [path]: 'Ready to play' }));
        
        // Play the audio
        audio.play()
          .then(() => {
            console.log(`Playing sound: ${path}`);
            setStatus(prev => ({ ...prev, [path]: 'Playing...' }));
          })
          .catch(err => {
            console.error(`Error playing sound ${path}:`, err);
            setStatus(prev => ({ ...prev, [path]: `Error: ${err.message || 'Unknown error'}` }));
          });
      }, { once: true });
      
      audio.addEventListener('ended', () => {
        console.log(`Sound ended: ${path}`);
        setStatus(prev => ({ ...prev, [path]: 'Played successfully' }));
      }, { once: true });
      
      audio.addEventListener('error', (e) => {
        console.error(`Error loading sound ${path}:`, e);
        setStatus(prev => ({ ...prev, [path]: `Error loading: ${e.type}` }));
      }, { once: true });
      
      // Start loading the audio
      audio.load();
      
    } catch (err) {
      console.error(`Error playing sound ${path}:`, err);
      setStatus(prev => ({ ...prev, [path]: `Error: ${err.message || 'Unknown error'}` }));
    }
  };
  
  // Alternative method to play sound with fetch + AudioContext
  const playWithContext = async (path: string) => {
    if (!audioContext) {
      setStatus(prev => ({ ...prev, [path]: 'No AudioContext available' }));
      return;
    }
    
    try {
      setStatus(prev => ({ ...prev, [path]: 'Fetching...' }));
      
      // Fetch the audio file
      const response = await fetch(path);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      setStatus(prev => ({ ...prev, [path]: 'Decoding...' }));
      
      // Get the array buffer of the audio data
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode the audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      setStatus(prev => ({ ...prev, [path]: 'Playing...' }));
      
      // Create a buffer source node
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      
      // Create a gain node for volume control
      const gainNode = audioContext.createGain();
      gainNode.gain.value = volume;
      
      // Connect the nodes: source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Play the sound
      source.start();
      
      // Update status when finished
      source.onended = () => {
        setStatus(prev => ({ ...prev, [path]: 'Played with AudioContext' }));
      };
    } catch (err) {
      console.error(`Error playing with AudioContext ${path}:`, err);
      setStatus(prev => ({ ...prev, [path]: `Context Error: ${err.message || 'Unknown error'}` }));
    }
  };
  
  // Play a sound with both methods for comparison
  const playSoundBothWays = (path: string) => {
    // Reset status for this sound
    setStatus(prev => ({ ...prev, [path]: 'Starting playback...' }));
    
    // Try standard Audio API first
    playSound(path);
    
    // After a short delay, try AudioContext API if the first one failed
    setTimeout(() => {
      const currentStatus = status[path];
      if (currentStatus && (currentStatus.includes('Error') || currentStatus === 'Loading...')) {
        console.log(`Fallback to AudioContext for ${path}`);
        playWithContext(path);
      }
    }, 1000);
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <h1 className="text-3xl font-bold mb-6">Sound System Test</h1>
      
      <div className="bg-black/10 p-4 mb-6 rounded-md">
        <h2 className="text-xl font-semibold mb-2">Audio System Status</h2>
        <div className="flex flex-col gap-2 mb-4">
          <div>
            <strong>AudioContext:</strong> {audioContext ? audioContext.state : 'Not available'}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleInitAudio} className="bg-blue-600 hover:bg-blue-700">
              Initialize Audio System
            </Button>
            <span className="text-sm opacity-70">
              (Click this first!)
            </span>
          </div>
        </div>
        
        <div className="mb-4">
          <h3 className="font-medium mb-2">Volume: {Math.round(volume * 100)}%</h3>
          <Slider
            value={[volume * 100]}
            onValueChange={(values) => setVolume(values[0] / 100)}
            min={0}
            max={100}
            step={1}
            className="mb-2"
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Sound Files</h2>
        <p className="text-sm opacity-70 mb-4">
          Click on any sound to test it. If you don't hear anything, try the "Initialize Audio System" button first.
        </p>
        
        {SOUND_FILES.map((sound) => (
          <div key={sound.path} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 border border-gray-200 rounded-md">
            <div className="flex-1">
              <div className="font-medium">{sound.name}</div>
              <div className="text-sm opacity-70">{sound.path}</div>
            </div>
            <div className="text-sm">
              {status[sound.path] || 'Not played yet'}
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => playSound(sound.path)} 
                variant="outline" 
                className="text-sm"
              >
                Play
              </Button>
              <Button 
                onClick={() => playWithContext(sound.path)} 
                variant="outline" 
                className="text-sm"
              >
                Context API
              </Button>
              <Button 
                onClick={() => playSoundBothWays(sound.path)} 
                variant="default" 
                className="text-sm"
              >
                Try Both
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-8 p-4 bg-yellow-100 text-yellow-800 rounded-md">
        <h3 className="font-bold mb-2">Troubleshooting</h3>
        <ul className="list-disc list-inside space-y-2">
          <li>Make sure your device volume is turned up</li>
          <li>iOS requires user interaction before playing sounds (tap the Initialize button)</li>
          <li>Some browsers block autoplay until user interacts with the page</li>
          <li>Check browser console for errors related to loading sound files</li>
          <li>If one sound works but others don't, there might be path issues</li>
        </ul>
      </div>
    </div>
  );
}